import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { PlusCircleIcon } from '@heroicons/react/24/solid';
import { Eye } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { deleteEvent } from '../moduleServices/planner_events_api';
import PlannerEventsList from './viewComponents/PlannerEventsList';
import ModalEventForm from './viewComponents/ModalEventForm';
import SpinnerLoad from '../moduleComponents/SpinnerLoad';
import CalendarFilterDropdown, {
  CALENDAR_FILTER_DROPDOWN_WRAP_CLASSNAME,
} from '../tab_todo/viewComponents/CalendarFilterDropdown';
import {
  loadMultiCalendarFilterFromStorage,
  persistMultiCalendarFilter,
  coerceMultiCalendarFilter,
  multiCalendarFiltersEqual,
} from '../moduleServices/planner_calendar_multi_filter';
import { PLANNER_TIME_FORMAT_24 } from '../moduleServices/planner_time_format';

// ##### LocalStorage key for calendar filter
const STORAGE_KEY_CALENDAR_FILTER = 'planner_events_calendar_filter';

function getStoredCalendarFilter() {
  return loadMultiCalendarFilterFromStorage(STORAGE_KEY_CALENDAR_FILTER);
}

function DisplayOption({ checked, label, onClick }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onClick}
      className="flex w-full items-center justify-between gap-3 px-2.5 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50"
    >
      <span>{label}</span>
      <span
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-blue-600' : 'bg-gray-300'
        }`}
        aria-hidden
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </span>
    </button>
  );
}

function DisplayOptions({ showWeekday, onWeekdayChange, showHour, onHourChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label="Display options"
        aria-expanded={open}
        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 shadow-sm hover:bg-gray-50"
      >
        <Eye className="h-4 w-4" strokeWidth={2} />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-52 rounded-md border border-gray-200 bg-white py-1 shadow">
          <DisplayOption
            checked={showWeekday}
            label="Show weekday"
            onClick={() => onWeekdayChange(!showWeekday)}
          />
          <DisplayOption
            checked={showHour}
            label="Show hour"
            onClick={() => onHourChange(!showHour)}
          />
        </div>
      )}
    </div>
  );
}

const MainEvents = ({
  events,
  isEventsLoading,
  refreshEvents,
  updateEventsOptimistically,
  calendars,
  fadePastEvents = true,
  timeFormatPreference = PLANNER_TIME_FORMAT_24,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showWeekday, setShowWeekday] = useState(true);
  const [showHour, setShowHour] = useState(false);
  // Use selectedEventId instead of editEvent state
  const [selectedEventId, setSelectedEventId] = useState(null);
  
  // ##### Calendar filter (multi-select, same pattern as calendar tab)
  const [selectedCalendarIds, setSelectedCalendarIdsState] = useState(getStoredCalendarFilter);

  const setSelectedCalendarIds = useCallback((ids) => {
    setSelectedCalendarIdsState(ids);
    persistMultiCalendarFilter(STORAGE_KEY_CALENDAR_FILTER, ids);
  }, []);

  useEffect(() => {
    const next = coerceMultiCalendarFilter(selectedCalendarIds, calendars);
    if (multiCalendarFiltersEqual(selectedCalendarIds, next)) return;
    setSelectedCalendarIds(next);
  }, [calendars, selectedCalendarIds, setSelectedCalendarIds]);

  // Derive selectedEvent from events array (single source of truth)
  const selectedEvent = useMemo(() => {
    if (!selectedEventId) return null;
    return events.find(e => e.id === selectedEventId) || null;
  }, [events, selectedEventId]);

  const defaultCalendarIdForNewEvent = useMemo(() => {
    if (selectedCalendarIds == null) return null;
    if (selectedCalendarIds.length === 0) return null;
    return selectedCalendarIds[0];
  }, [selectedCalendarIds]);
  
  const eventsExcludingTodoCalendars = useMemo(() => {
    const allowed = new Set(calendars.map((c) => c.id));
    return events.filter((e) => allowed.has(e.calendar?.id ?? NaN));
  }, [events, calendars]);

  // ##### Filter events by selected calendars (null = show all non–to-do calendars)
  const filteredEvents = useMemo(() => {
    if (selectedCalendarIds === null) {
      return eventsExcludingTodoCalendars;
    }
    if (selectedCalendarIds.length === 0) {
      return [];
    }
    const allowed = new Set(selectedCalendarIds);
    return eventsExcludingTodoCalendars.filter((e) => allowed.has(e.calendar?.id ?? NaN));
  }, [eventsExcludingTodoCalendars, selectedCalendarIds]);

  const searchedEvents = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return filteredEvents;
    return filteredEvents.filter((event) => {
      const title = (event.title || '').toLowerCase();
      const description = (event.description || '').toLowerCase();
      return title.includes(term) || description.includes(term);
    });
  }, [filteredEvents, search]);

  const handleNewEvent = () => {
    setSelectedEventId(null);
    setIsModalOpen(true);
  };

  const handleEditEvent = (event) => {
    setSelectedEventId(event.id);
    setIsModalOpen(true);
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await deleteEvent(eventId);
      await refreshEvents();
      // If deleted event was selected, close modal
      if (selectedEventId === eventId) {
        setIsModalOpen(false);
        setSelectedEventId(null);
      }
    } catch (error) {
      throw error;
    }
  };

  // Simplified optimistic update
  const handleOptimisticUpdate = (updatedEventData) => {
    if (updatedEventData && updatedEventData.id) {
      updateEventsOptimistically(updatedEventData);
    }
  };

  // Simplified modal close
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedEventId(null);
  };

  if (isEventsLoading && !isModalOpen) {
    return (
      <div className="p-2 flex justify-center items-center min-h-[400px]">
        <SpinnerLoad />
      </div>
    );
  }

  return (
    <>
      <div>
        {/* ##### Calendar multi-filter — aligned right (matches calendar tab) */}
        {!isEventsLoading && (
          <div className="flex w-full items-center justify-end gap-2 px-2 mt-2 pb-0">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search events"
              aria-label="Search events by name or description"
              className="w-full max-w-[14rem] min-w-0 rounded-full border border-gray-300 bg-white px-3 py-1 text-sm text-gray-700 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            />
            <DisplayOptions
              showWeekday={showWeekday}
              onWeekdayChange={setShowWeekday}
              showHour={showHour}
              onHourChange={setShowHour}
            />
            <div className={CALENDAR_FILTER_DROPDOWN_WRAP_CLASSNAME}>
              <CalendarFilterDropdown
                multiple
                fullWidthTrigger={false}
                calendars={calendars}
                value={selectedCalendarIds}
                onChange={setSelectedCalendarIds}
              />
            </div>
          </div>
        )}
        
        {/* ##### Events List */}
        {searchedEvents.length === 0 && filteredEvents.length > 0 ? (
          <div className="px-2 py-8 text-center text-gray-500 text-sm">
            No events match this search.
          </div>
        ) : filteredEvents.length === 0 && eventsExcludingTodoCalendars.length > 0 ? (
          <div className="px-2 py-8 text-center text-gray-500 text-sm">
            No events in this calendar.
          </div>
        ) : (
          <div className="px-2">
            <PlannerEventsList
              events={searchedEvents}
              onEventClick={handleEditEvent}
              fadePastEvents={fadePastEvents}
              showWeekday={showWeekday}
              showHour={showHour}
              timeFormatPreference={timeFormatPreference}
            />
          </div>
        )}
      </div>

      {/* FAB Button Add */}
      <button
        onClick={handleNewEvent}
        className="fixed bottom-20 right-6 bg-blue-700 hover:bg-blue-800 text-white p-3 rounded-full shadow-lg"
        aria-label="Add New Event"
      >
        <PlusCircleIcon className="h-8 w-8" />
      </button>

      {/* Modal */}
      <ModalEventForm
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onOptimisticUpdate={handleOptimisticUpdate}
        editEvent={selectedEvent}
        onDelete={handleDeleteEvent}
        calendars={calendars}
        defaultCalendarId={defaultCalendarIdForNewEvent}
        timeFormatPreference={timeFormatPreference}
      />
    </>
  );
};

export default MainEvents;
